import type { Graphics } from 'pixi.js';
import { SIZE_PER_BEAT } from '../../../core/chart';
import { lerpColor } from '../../color';
import type { Body, WorldDress, WorldFx } from '../../world';

const TAU = Math.PI * 2;
const UP = -Math.PI / 2;
const TICKS = 12;
const LINK_SIDES = 6;

function toothRing(
  g: Graphics,
  x: number,
  y: number,
  inner: number,
  outer: number,
  teeth: number,
  rot: number,
  sx = 1,
  sy = 1,
): void {
  const step = TAU / teeth;
  const pts: number[] = [];
  const at = (r: number, a: number): void => {
    pts.push(x + Math.cos(a) * r * sx, y + Math.sin(a) * r * sy);
  };
  for (let i = 0; i < teeth; i++) {
    const a = rot + i * step;
    at(inner, a);
    at(outer, a + step * 0.16);
    at(outer, a + step * 0.46);
    at(inner, a + step * 0.62);
  }
  g.poly(pts, true);
}

function diamond(g: Graphics, x: number, y: number, along: number, across: number, a: number): void {
  const c = Math.cos(a);
  const s = Math.sin(a);
  g.poly([x + c * along, y + s * along, x - s * across, y + c * across, x - c * along, y - s * along, x + s * across, y - c * across], true);
}

function link(g: Graphics, x: number, y: number, rx: number, ry: number, a: number): void {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const pts: number[] = [];
  for (let j = 0; j < LINK_SIDES; j++) {
    const t = (j / LINK_SIDES) * TAU;
    const ex = Math.cos(t) * rx;
    const ey = Math.sin(t) * ry;
    pts.push(x + c * ex - s * ey, y + s * ex + c * ey);
  }
  g.poly(pts, true);
}

function crown(g: Graphics, body: Body, fill: number, shade: number, outline: number, alpha: number, width: number): void {
  const r = body.radius;
  const top = body.y - r * body.scaleY;
  g.circle(body.x, top - r * 0.2, r * 0.11).stroke({ width: Math.max(1, width * 0.55), color: outline, alpha: 0.85 * alpha });
  g.roundRect(body.x - r * 0.1, top - r * 0.13, r * 0.2, r * 0.17, r * 0.05).fill({ color: lerpColor(fill, shade, 0.5), alpha });
  g.stroke({ width: Math.max(1, width * 0.5), color: outline, alpha: 0.85 * alpha });
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    const dark = lerpColor(color, pal.ink, 0.5);
    const spin = (k.reduced ? 0 : k.time * 0.7) + body.rotation;
    toothRing(g, body.x, body.y, r * 0.56, r * 0.7, 12, spin, body.scaleX, body.scaleY);
    g.stroke({ width: Math.max(1, r * 0.05), color: dark, alpha: 0.38 * alpha, join: 'round' });
    g.ellipse(body.x, body.y, r * 0.3 * body.scaleX, r * 0.3 * body.scaleY).stroke({
      width: Math.max(1, r * 0.035),
      color: dark,
      alpha: 0.28 * alpha,
    });
    g.ellipse(body.x, body.y, r * 0.1 * body.scaleX, r * 0.1 * body.scaleY).fill({ color: dark, alpha: 0.6 * alpha });
    k.rimLight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const mark = lerpColor(fill, pal.ink, 0.62);
    crown(g, body, fill, pal.heldShade, k.outline, alpha, width);
    k.trace(body);
    g.fill({ color: fill, alpha });
    g.ellipse(body.x, body.y, r * 0.82 * body.scaleX, r * 0.82 * body.scaleY).fill({ color: lerpColor(fill, pal.heldLight, 0.3), alpha });
    g.stroke({ width: Math.max(1, r * 0.03), color: pal.heldShade, alpha: 0.6 * alpha });
    for (const major of [true, false]) {
      for (let i = 0; i < TICKS; i++) {
        if (i % 3 === 0 !== major) continue;
        const a = UP + (i / TICKS) * TAU;
        const r0 = major ? 0.58 : 0.66;
        const c = Math.cos(a) * body.scaleX;
        const s = Math.sin(a) * body.scaleY;
        g.moveTo(body.x + c * r * r0, body.y + s * r * r0).lineTo(body.x + c * r * 0.76, body.y + s * r * 0.76);
      }
      g.stroke({ width: Math.max(1, r * (major ? 0.06 : 0.03)), color: mark, alpha: (major ? 0.8 : 0.45) * alpha, cap: 'round' });
    }
    const beats = r / Math.max(1e-6, k.unit * SIZE_PER_BEAT);
    const hand = UP + (beats / TICKS) * TAU;
    const hc = Math.cos(hand);
    const hs = Math.sin(hand);
    g.moveTo(body.x - hc * r * 0.12 * body.scaleX, body.y - hs * r * 0.12 * body.scaleY).lineTo(
      body.x + hc * r * 0.64 * body.scaleX,
      body.y + hs * r * 0.64 * body.scaleY,
    );
    g.stroke({ width: Math.max(1.5, r * 0.07), color: pal.ink, alpha, cap: 'round' });
    g.circle(body.x, body.y, Math.max(1.2, r * 0.07)).fill({ color: pal.ink, alpha });
    k.rimLight(body, 0.55 * alpha);
    k.trace(body);
    g.stroke({ width: width * 0.8, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    const s = Math.max(3, k.unit * 0.02);
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.4), alpha: 0.5 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    g.circle(body.x, body.y, reach).stroke({ width: 1.2, color: pal.path, alpha: 0.45 * alpha });
    for (let i = 0; i < TICKS; i++) {
      if (i % 3 === 0) continue;
      const a = UP + (i / TICKS) * TAU;
      g.moveTo(body.x + Math.cos(a) * (reach - s * 0.9), body.y + Math.sin(a) * (reach - s * 0.9)).lineTo(
        body.x + Math.cos(a) * (reach + s * 0.3),
        body.y + Math.sin(a) * (reach + s * 0.3),
      );
    }
    g.stroke({ width: 1.4, color: pal.rim, alpha: 0.6 * alpha, cap: 'round' });
    for (let i = 0; i < 4; i++) {
      const a = UP + (i * Math.PI) / 2;
      diamond(g, body.x + Math.cos(a) * reach, body.y + Math.sin(a) * reach, s * 1.3, s * 0.7, a);
    }
    g.fill({ color: pal.spot, alpha: 0.85 * alpha });
    const sweep = UP + spin * 4;
    g.circle(body.x + Math.cos(sweep) * reach, body.y + Math.sin(sweep) * reach, s * 0.45).fill({ color: pal.held, alpha: 0.7 * alpha });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.85 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha, width) {
    const { g, pal } = k;
    const r = radius * 1.15;
    g.circle(x, y, r);
    g.fill(on ? { color: last ? pal.spot : pal.held, alpha } : { color: lerpColor(pal.bg, pal.held, 0.18), alpha: 0.7 * alpha });
    g.stroke({ width: width * 0.8, color: pal.rim, alpha: (on ? 0.9 : 0.5) * alpha });
    if (!on) return;
    g.circle(x - r * 0.32, y - r * 0.32, r * 0.28).fill({ color: pal.heldLight, alpha: 0.8 * alpha });
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const step = Math.max(7, k.unit * 0.04);
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const n = Math.min(60, Math.floor(Math.hypot(dx, dy) / step));
      const a = Math.atan2(dy, dx);
      for (let j = 1; j < n; j++) {
        const t = j / n;
        const x = x0 + dx * t;
        const y = y0 + dy * t;
        if (j % 2 === 0) link(g, x, y, step * 0.42, step * 0.22, a);
        else g.moveTo(x - Math.cos(a) * step * 0.3, y - Math.sin(a) * step * 0.3).lineTo(x + Math.cos(a) * step * 0.3, y + Math.sin(a) * step * 0.3);
      }
    }
    g.stroke({ width: 1.1, color: pal.path, alpha: Math.min(1, alpha * 2.2), cap: 'round' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 2) {
      const c = Math.cos(rot);
      const s = Math.sin(rot);
      const len = size * 2.2;
      g.moveTo(x - c * len * 0.5, y - s * len * 0.5);
      for (let j = 1; j <= 6; j++) {
        const t = j / 6 - 0.5;
        const side = (j % 2 === 0 ? -1 : 1) * size * 0.55;
        g.lineTo(x + c * len * t - s * side, y + s * len * t + c * side);
      }
      g.stroke({ width: Math.max(1, size * 0.32), color, alpha, cap: 'round', join: 'round' });
      return;
    }
    g.star(x, y, 8, size * 1.25, size * 0.95, rot);
    g.fill({ color, alpha });
    g.circle(x, y, size * 0.38).fill({ color: lerpColor(color, 0x000000, 0.55), alpha: 0.7 * alpha });
  },

  milestone(g, m, pal) {
    g.circle(m.x, m.y, m.r).stroke({ width: m.width * 1.6, color: pal.held, alpha: m.fade * 0.3 });
    g.circle(m.x, m.y, m.r).stroke({ width: m.width * 0.6, color: pal.heldLight, alpha: m.fade * 0.55 });
    toothRing(g, m.x, m.y, m.r * 1.12, m.r * 1.22, 24, m.grow * TAU);
    g.stroke({ width: m.width * 0.5, color: pal.spot, alpha: m.fade * 0.4, join: 'round' });
  },

  trail(g, x, y, size, angle) {
    diamond(g, x, y, size * 1.6, size * 0.45, angle);
  },
};
